"use client";

import React from "react";
import { Edit, FileText, X } from "lucide-react";
import { StructureDefinition } from "./types";
import { type Format } from "@/lib/clients/formats";

interface OptionTagsProps {
  promptValue?: string;
  selectedStructureDefinitionId?: string;
  structureDefinitions?: StructureDefinition[];
  formats?: Format[];
  onClearPrompt: () => void;
  onClearStructureDefinition: () => void;
}

// popアニメーション用のスタイル
const popAnimationStyles = `
  @keyframes popIn {
    0% {
      opacity: 0;
      transform: scale(0.9);
    }
    50% {
      transform: scale(1.02);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .pop-in {
    animation: popIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }
`;

export const OptionTags: React.FC<OptionTagsProps> = ({
  promptValue,
  selectedStructureDefinitionId,
  structureDefinitions = [],
  formats = [],
  onClearPrompt,
  onClearStructureDefinition,
}) => {
  // フォーマット/構造定義の名前を取得
  const getFormatOrStructureDefinitionName = () => {
    if (selectedStructureDefinitionId) {
      // まずformatsから検索
      const format = formats.find(f => f.id === selectedStructureDefinitionId);
      if (format) {
        return format.name;
      }
      
      // 次にstructureDefinitionsから検索
      const definition = structureDefinitions.find(def => def.id === selectedStructureDefinitionId);
      if (definition) {
        return definition.name;
      }
      
      return "不明なフォーマット";
    }
    
    // デフォルトフォーマット/構造定義を表示
    if (formats.length > 0) {
      return `${formats[0].name} (フォーマット)`;
    }
    
    if (structureDefinitions.length > 0) {
      // 作成日時で降順ソート（最新が最初）
      const sortedDefinitions = [...structureDefinitions].sort((a, b) => {
        if (!a.created_at && !b.created_at) return 0;
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      return `${sortedDefinitions[0].name} (構造定義)`;
    }
    
    return null;
  };

  const hasPrompt = Boolean(promptValue?.trim());
  const hasFormatOrStructureDefinition = Boolean(selectedStructureDefinitionId) || formats.length > 0 || structureDefinitions.length > 0;
  const hasAnyTags = hasPrompt || hasFormatOrStructureDefinition;

  return (
    <>
      {/* スタイルを適用 */}
      <style>{popAnimationStyles}</style>
      
      <div className="mb-3 min-h-[40px] flex items-start">
        <div className="w-full overflow-hidden">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {/* プロンプトタグ（カスタムプロンプトまたはライブラリプロンプト） */}
            {hasPrompt && (
              <div className="group relative flex items-center space-x-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg px-3 py-2 transition-all duration-200 flex-shrink-0 pop-in">
                <Edit className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <span className="text-xs font-medium text-primary truncate max-w-32">
                  {promptValue && promptValue.length > 20 
                    ? `${promptValue.substring(0, 20)}...` 
                    : promptValue}
                </span>
                
                {/* ホバー時の削除ボタン */}
                <button
                  onClick={onClearPrompt}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-0.5 hover:bg-primary/30 rounded-full ml-1 flex-shrink-0"
                  aria-label="プロンプト設定を削除"
                >
                  <X className="h-3 w-3 text-primary" />
                </button>
              </div>
            )}

            {/* フォーマット/構造定義タグ */}
            {hasFormatOrStructureDefinition && (
              <div className="group relative flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg px-3 py-2 transition-all duration-200 flex-shrink-0 pop-in">
                <FileText className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-500 truncate max-w-32">
                  {getFormatOrStructureDefinitionName()}
                </span>
                
                {/* ホバー時の削除ボタン（デフォルトフォーマット/構造定義の場合は非表示） */}
                {selectedStructureDefinitionId && (
                  <button
                    onClick={onClearStructureDefinition}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-0.5 hover:bg-secondary/30 rounded-full ml-1 flex-shrink-0"
                    aria-label="フォーマット設定を削除"
                  >
                    <X className="h-3 w-3 text-gray-700" />
                  </button>
                )}
              </div>
            )}

            {/* タグがない場合の空白スペース（レイアウトシフト防止） */}
            {!hasAnyTags && (
              <div className="h-[32px] w-full opacity-0" aria-hidden="true" />
            )}
          </div>
        </div>
      </div>
    </>
  );
};